import { Link } from '@inertiajs/react'
import HeaderStat, { formatStatDate } from './HeaderStat'
import { formatVND } from './formatters'
import { timeAgo } from '@/lib/time'

// AppLayout header shared by the three Top Down edit pages: plan name with
// the Edit Plan link under it, then Scope / Target / Period. The overall
// editor opens its modal in place (onEditPlan); the channel pages link back
// to it (editPlanHref).
export default function EditorHeader({
  name,
  beginDate,
  endDate,
  orgNodeName,
  revenueTarget,
  currency,
  onEditPlan,
  editPlanHref,
  planPeriod,
  actualsMonths,
  fullYearTarget,
}: {
  name: string
  beginDate: string
  endDate: string
  orgNodeName: string | null
  revenueTarget: number
  currency: string | null
  onEditPlan?: () => void
  editPlanHref?: string
  // A revision plans only the remaining months: the target below covers
  // planPeriod only, and fullYearTarget adds the actual months back in.
  planPeriod?: { start: string; end: string }
  actualsMonths?: number | null
  fullYearTarget?: number | null
}) {
  const linkClass = 'block text-[11px] font-semibold text-primary hover:underline'
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, width: '100%', paddingRight: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
        <div>
          <h1 style={{ fontSize: 16, fontWeight: 700, color: 'var(--foreground)', margin: 0, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>
            {name}
          </h1>
          {onEditPlan && (
            <button type="button" onClick={onEditPlan} className={linkClass}>
              Edit Plan
            </button>
          )}
          {!onEditPlan && editPlanHref && (
            <Link href={editPlanHref} className={linkClass}>
              Edit Plan
            </Link>
          )}
        </div>
        {orgNodeName && <HeaderStat label="Scope" value={orgNodeName} />}
        <HeaderStat
          label={actualsMonths ? 'Remaining Target' : 'Target'}
          value={revenueTarget > 0 ? formatVND(revenueTarget, currency) : 'Not set'}
        />
        {actualsMonths && fullYearTarget != null && (
          <HeaderStat label="Full Target" value={formatVND(fullYearTarget, currency)} />
        )}
        <HeaderStat
          label={actualsMonths ? 'Remaining Period' : 'Period'}
          value={actualsMonths && planPeriod
            ? `${formatStatDate(planPeriod.start)} – ${formatStatDate(planPeriod.end)}`
            : `${formatStatDate(beginDate)} – ${formatStatDate(endDate)}`}
        />
      </div>
    </div>
  )
}

// Banner above the editor while the draft is a revision of a published plan.
export function RevisionBanner() {
  return (
    <div
      className="mb-4 rounded-lg border px-4 py-2.5 text-sm text-foreground"
      style={{ background: 'var(--info-surface)', borderColor: 'var(--border)' }}
    >
      You are editing a revision. Published numbers stay live until a reviewer approves.
    </div>
  )
}

// Someone else's unsubmitted edits live in this same shared draft. Named so a
// person opening the plan is not surprised by numbers they did not enter.
export function DraftActivityBanner({ activity }: { activity: { by: string; at: string } }) {
  return (
    <div
      className="mb-4 rounded-lg border px-4 py-2.5 text-sm text-foreground"
      style={{ background: 'var(--warning-surface)', borderColor: 'var(--border)' }}
    >
      <span className="font-semibold">{activity.by}</span> has unsubmitted changes in this draft
      (last edit {timeAgo(activity.at)}). You are both editing the same draft.
    </div>
  )
}

